import { useState, useRef, useEffect, useMemo, useLayoutEffect } from 'react';
import type { KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check, X, Search } from 'lucide-react';

export interface SSOption {
  value: string | number;
  label: string;
  hint?: string;
}

interface Props {
  value: string | number | '' | null | undefined;
  onChange: (v: string | number | '') => void;
  options: SSOption[];
  placeholder?: string;
  loading?: boolean;
  disabled?: boolean;
  className?: string;
  allowClear?: boolean;
}

/**
 * Single-select dropdown with type-to-filter. Use anywhere a native <select>
 * has too many options (merchants, warehouses, couriers, users, branches).
 */
export function SearchableSelect({ value, onChange, options, placeholder = 'Select…', loading, disabled, className = '', allowClear = true }: Props) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const wrap = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + 4, left: r.left, width: r.width });
    const onScroll = () => {
      if (!btnRef.current) return;
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: r.left, width: r.width });
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => { window.removeEventListener('scroll', onScroll, true); window.removeEventListener('resize', onScroll); };
  }, [open]);
  const selected = useMemo(() => options.find(o => String(o.value) === String(value)) ?? null, [value, options]);

  useEffect(() => {
    if (!open) return;
    const fn = (e: MouseEvent) => {
      const t = e.target as Node;
      if (wrap.current?.contains(t)) return;
      if (popRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, [open]);

  const filtered = useMemo(() => {
    if (!q.trim()) return options;
    const needle = q.toLowerCase();
    return options.filter(o => o.label.toLowerCase().includes(needle) || (o.hint ?? '').toLowerCase().includes(needle) || String(o.value).includes(needle));
  }, [q, options]);

  // When the dropdown opens (or the filter changes), highlight the currently
  // selected option if visible, otherwise the first row — so the keyboard has a
  // sensible starting point.
  useEffect(() => {
    if (!open) return;
    const selIdx = filtered.findIndex(o => String(o.value) === String(value));
    setActive(selIdx >= 0 ? selIdx : 0);
  }, [open, filtered, value]);

  // Keep the highlighted row scrolled into view as the user arrows through.
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  // Return focus to the trigger button whenever the popup closes from the keyboard
  // or a selection. The popup is portaled to <body>, so if focus stays on the
  // (about-to-unmount) search input / option row, the browser drops focus to <body>
  // and the next Tab restarts from the top of the page. Re-anchoring on the button
  // keeps Tab / Shift+Tab flowing smoothly to the next / previous field.
  const focusBtn = () => { btnRef.current?.focus(); };
  const commit = (o: SSOption) => { onChange(o.value); setOpen(false); setQ(''); focusBtn(); };

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive(a => Math.min(a + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive(a => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const o = filtered[active];
      if (o) commit(o);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false); setQ(''); focusBtn();
    } else if (e.key === 'Tab') {
      // Tab commits the highlighted option (forward only) and closes, then re-anchors
      // focus on the trigger button WITHOUT preventing default — so the browser's own
      // Tab / Shift+Tab immediately advances to the next / previous field. This is what
      // makes single-booking data entry flow field-to-field without reaching for the mouse.
      if (!e.shiftKey) { const o = filtered[active]; if (o) onChange(o.value); }
      setOpen(false); setQ('');
      focusBtn();
    } else if (e.key === 'Home') {
      e.preventDefault();
      setActive(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      setActive(filtered.length - 1);
    }
  };

  // Open with the keyboard the way a native <select> does: ArrowUp/Down open the list,
  // and typing a character opens it and seeds the filter. (Enter/Space already toggle via
  // the button's native click.) Tab is deliberately NOT intercepted here so it keeps
  // moving focus to the next field when the control is closed.
  const onBtnKeyDown = (e: KeyboardEvent) => {
    if (disabled) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
    } else if (e.key.length === 1 && e.key !== ' ' && !e.altKey && !e.ctrlKey && !e.metaKey) {
      setOpen(true);
      setQ(e.key);
      e.preventDefault();
    }
  };

  return (
    <div ref={wrap} className={`relative ${className}`}>
      <button ref={btnRef} type="button" disabled={disabled}
        onClick={() => setOpen(o => !o)}
        onKeyDown={onBtnKeyDown}
        className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm bg-white flex items-center justify-between gap-2 hover:border-slate-400 disabled:bg-slate-50 disabled:cursor-not-allowed transition">
        <span className={selected ? 'text-slate-900' : 'text-slate-400'}>
          {selected ? selected.label : placeholder}
        </span>
        <div className="flex items-center gap-1">
          {allowClear && selected && !disabled && (
            <X size={13} className="text-slate-400 hover:text-slate-700" onClick={(e) => { e.stopPropagation(); onChange(''); }} />
          )}
          <ChevronDown size={14} className={`text-slate-500 transition ${open ? 'rotate-180' : ''}`} />
        </div>
      </button>
      {open && pos && createPortal(
        <div ref={popRef} className="fixed z-[10050] bg-white border border-slate-200 rounded-lg shadow-xl max-h-80 overflow-hidden" style={{ top: pos.top, left: pos.left, width: pos.width }}>
          <div className="p-2 border-b border-slate-100 flex items-center gap-2">
            <Search size={13} className="text-slate-400" />
            <input value={q} autoFocus onChange={(e) => setQ(e.target.value)} onKeyDown={onKeyDown} placeholder="Search…" className="flex-1 outline-none text-sm" />
          </div>
          <div className="overflow-y-auto max-h-64">
            {loading && <div className="p-3 text-xs text-slate-400 text-center">Loading…</div>}
            {!loading && filtered.length === 0 && <div className="p-3 text-xs text-slate-400 text-center">No matches</div>}
            {filtered.map((o, i) => {
              const sel = String(o.value) === String(value);
              const isActive = i === active;
              return (
                <button key={String(o.value)} ref={isActive ? activeRef : undefined}
                  onClick={() => commit(o)} onMouseEnter={() => setActive(i)}
                  className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 ${isActive ? 'bg-slate-100' : ''} ${sel ? 'bg-indigo-50' : ''}`}>
                  <div className={`w-4 h-4 rounded-full border ${sel ? 'bg-indigo-600 border-indigo-600 flex items-center justify-center' : 'border-slate-300'}`}>
                    {sel && <Check size={10} className="text-white" />}
                  </div>
                  <span className="flex-1">{o.label}</span>
                  {o.hint && <span className="text-xs text-slate-400">{o.hint}</span>}
                </button>
              );
            })}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
